import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

function monthStr(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 7);
}

export default function Services() {
  const [serviceLines, setServiceLines] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clientFinancials, setClientFinancials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const [slRes, projRes, invRes, cfRes] = await Promise.all([
      supabase.from("service_lines").select("*").order("name"),
      supabase.from("projects").select("id, client_id, service_line_id"),
      supabase.from("invoices").select("id, client_id, project_id, amount, invoice_date"),
      supabase.from("client_financials").select("*").in("month", [`${monthStr(0)}-01`, `${monthStr(-1)}-01`]),
    ]);
    setServiceLines(slRes.data || []);
    setProjects(projRes.data || []);
    setInvoices(invRes.data || []);
    setClientFinancials(cfRes.data || []);
    setLoading(false);
  }

  const rows = useMemo(() => {
    const thisMonth = monthStr(0);
    const lastMonth = monthStr(-1);

    const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));

    function revenueFor(month, serviceLineId, clientId) {
      return invoices
        .filter((i) => i.invoice_date?.slice(0, 7) === month)
        .filter((i) => {
          const proj = i.project_id ? projectById[i.project_id] : null;
          if (!proj) return false;
          if (serviceLineId !== undefined && proj.service_line_id !== serviceLineId) return false;
          if (clientId !== undefined && proj.client_id !== clientId) return false;
          return true;
        })
        .reduce((sum, i) => sum + Number(i.amount), 0);
    }

    const clientIds = [...new Set(projects.map((p) => p.client_id))];
    const clientTotalRevenue = Object.fromEntries(
      clientIds.map((cid) => [cid, revenueFor(thisMonth, undefined, cid)])
    );
    const clientCost = Object.fromEntries(
      clientFinancials.filter((f) => f.month === `${thisMonth}-01`).map((f) => [f.client_id, Number(f.cost)])
    );

    return serviceLines.map((sl) => {
      const serviceClients = [...new Set(projects.filter((p) => p.service_line_id === sl.id).map((p) => p.client_id))];
      const revenueThis = revenueFor(thisMonth, sl.id);
      const revenueLast = revenueFor(lastMonth, sl.id);
      const growth = revenueLast > 0 ? Math.round(((revenueThis - revenueLast) / revenueLast) * 100) : null;

      let attributedCost = 0;
      serviceClients.forEach((cid) => {
        const clientRevThisService = revenueFor(thisMonth, sl.id, cid);
        const clientRevTotal = clientTotalRevenue[cid] || 0;
        const cost = clientCost[cid] || 0;
        if (clientRevTotal > 0) {
          attributedCost += cost * (clientRevThisService / clientRevTotal);
        }
      });
      const margin = revenueThis > 0 ? Math.round(((revenueThis - attributedCost) / revenueThis) * 100) : null;

      return {
        id: sl.id,
        name: sl.name,
        clients: serviceClients.length,
        revenue: revenueThis,
        margin,
        growth,
      };
    });
  }, [serviceLines, projects, invoices, clientFinancials]);

  const best = rows.filter((r) => r.margin !== null).sort((a, b) => b.margin - a.margin)[0];
  const fastest = rows.filter((r) => r.growth !== null).sort((a, b) => b.growth - a.growth)[0];
  const attention = rows.filter((r) => r.growth !== null && r.growth < 0).sort((a, b) => a.growth - b.growth)[0];

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Services</h1>
      <p className="text-sm text-muted-foreground mb-5">Revenue, margin, and growth by service line</p>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <Callout label="Best margin" value={best ? `${best.name}, ${best.margin}%` : "Not enough data yet"} />
        <Callout label="Fastest growing" value={fastest ? `${fastest.name}, +${fastest.growth}%` : "Not enough data yet"} />
        <Callout label="Needs attention" value={attention ? `${attention.name}, ${attention.growth}% growth` : "None flagged"} />
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
          Revenue by service, {monthStr(0)}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <Th>Service</Th>
              <Th align="right">Revenue</Th>
              <Th align="right">Clients</Th>
              <Th align="right">Margin</Th>
              <Th align="right">Growth</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="px-5 py-3.5 font-medium text-foreground">{r.name}</td>
                <td className="px-5 py-3.5 text-right">₹{r.revenue.toLocaleString("en-IN")}</td>
                <td className="px-5 py-3.5 text-right">{r.clients}</td>
                <td className="px-5 py-3.5 text-right">{r.margin !== null ? `${r.margin}%` : "-"}</td>
                <td className="px-5 py-3.5 text-right">
                  {r.growth !== null ? (
                    <span className={r.growth >= 0 ? "text-primary" : "text-destructive"}>
                      {r.growth >= 0 ? "+" : ""}
                      {r.growth}%
                    </span>
                  ) : (
                    "-"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-muted-foreground mt-3">
        Margin is apportioned from each client's total monthly cost based on how much of their revenue this month
        came from that service, since cost is logged per client, not per service line directly.
      </p>
    </div>
  );
}

function Callout({ label, value }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th className={`text-${align || "left"} font-mono text-[10.5px] uppercase tracking-wide text-muted-foreground px-5 py-3`}>
      {children}
    </th>
  );
}
